/**
 * "Create Earthworks Job" on an accepted Earthworks quote (EW-2,
 * fieldiq-api POST /quotes/:id/create-ew-job).
 *
 * Well Testing's Create Well Testing Job with a SITE where Well Testing asks
 * for a well (ew_jobs.site_id is NOT NULL) and a JOB TYPE (the EW catalog's,
 * from GET /earthworks/meta). The site list is the quote customer's sites. The
 * start day and PO are optional. The job carries the quote's lines.
 *
 * A press with no site is SAID, not sent: SITE_REQUIRED's sentence, in the
 * quote screen's refusal notice. Every API refusal goes the same way
 * (ewQuoteRefusal); success hands the job to the screen, which opens it — a
 * repeat (alreadyCreated) opens the job that already exists.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useEwMeta, useEwSites } from '../../hooks/useEarthworks';
import { ewQuoteRefusal } from '../../lib/ew-quotes';
import type { CreateEwJobResponse } from '../../types/earthworks';

const SELECT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white max-w-[16rem]';
const INPUT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white';
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function CreateEwJobAction({
  quoteId,
  customerId,
  onCreated,
  onRefused,
}: {
  quoteId: string;
  customerId: string | null | undefined;
  onCreated: (data: CreateEwJobResponse['data']) => void;
  onRefused: (refusal: { code?: string; error: string }) => void;
}) {
  const meta = useEwMeta();
  const jobTypes = meta.data?.data?.jobTypes ?? [];
  const [siteId, setSiteId] = useState('');
  const [jobType, setJobType] = useState('');
  const [day, setDay] = useState('');
  const [po, setPo] = useState('');
  const type = jobType || jobTypes[0]?.value || '';

  const create = useMutation({
    mutationFn: () => api.quotes.createEwJob(quoteId, {
      siteId,
      jobType: type,
      ...(day ? { scheduledStart: day } : {}),
      ...(po.trim() ? { poNumber: po.trim() } : {}),
    }),
    onSuccess: (res) => {
      const d = res.data?.data;
      if (d?.job?.id) onCreated(d);
    },
    onError: (err) => onRefused(ewQuoteRefusal(err, 'Failed to create the Earthworks job')),
  });

  const press = () => {
    // No customer on the quote: the API's QUOTE_CUSTOMER_REQUIRED is the reason, so ask it.
    if (!siteId && customerId) {
      onRefused(ewQuoteRefusal({ response: { status: 400, data: { code: 'SITE_REQUIRED' } } }, ''));
      return;
    }
    create.mutate();
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="create-ew-job">
      {customerId && <SitePicker customerId={customerId} siteId={siteId} onSite={setSiteId} />}
      <select aria-label="Earthworks job type" value={type} onChange={(e) => setJobType(e.target.value)} className={SELECT}>
        {jobTypes.length === 0 && <option value="">{meta.isLoading ? 'Loading job types…' : 'No job types'}</option>}
        {jobTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <input type="date" aria-label="Earthworks job start (optional)" value={day} onChange={(e) => setDay(e.target.value)} className={INPUT} />
      <input aria-label="Customer PO (optional)" placeholder="PO (optional)" value={po} onChange={(e) => setPo(e.target.value)} className={`${INPUT} w-32`} />
      <button onClick={press} disabled={create.isPending} className={ACTION_PRIMARY}>
        {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        Create Earthworks Job
      </button>
    </span>
  );
}

/** The quote customer's sites. Mounted only with a customer. */
function SitePicker({ customerId, siteId, onSite }: { customerId: string; siteId: string; onSite: (id: string) => void }) {
  const sites = useEwSites({ customerId });
  const rows = sites.data?.data ?? [];
  const empty = sites.isLoading ? 'Loading sites…' : sites.isError ? 'Sites could not be loaded' : rows.length ? 'Choose the site…' : 'No sites for this customer';
  return (
    <select aria-label="Earthworks job site" value={siteId} onChange={(e) => onSite(e.target.value)} className={SELECT}>
      <option value="">{empty}</option>
      {rows.map((s) => <option key={s.id} value={s.id}>{s.name}{s.kindLabel ? ` (${s.kindLabel})` : ''}</option>)}
    </select>
  );
}
